import { ReactNode } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { Auth0ProviderOptions } from '@auth0/auth0-react';

import { Loading } from '@crossnokaye/ux-common';

import { AUTH_SCOPE_READ_ONLY } from '@/constants';
import { SUPPORT_CONTACT_NUMBER } from '@/constants';
import { getCookieDomain } from '@/helpers/cookie-domain';
import { useAppConfig } from '@/hooks/use-app-config';
import { isAgentProxyMode } from '@/utils/agent-proxy';

import { AgentProxyAuthContextProvider } from './agent-proxy-auth-context-provider';
import { AuthContextProvider } from './auth-context-provider';
import { AuthLoadingBoundary } from './auth-loading-boundary';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { data: envConfig, isLoading, isError, error } = useAppConfig();

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !envConfig) {
    console.error('Failed to load Auth config', error);
    return (
      <div>{`Authentication unavailable. Please contact support at ${SUPPORT_CONTACT_NUMBER}.`}</div>
    );
  }

  // AgentProxy mode: Skip Auth0, use mock user directly
  if (isAgentProxyMode(envConfig)) {
    return (
      <AgentProxyAuthContextProvider config={envConfig}>{children}</AgentProxyAuthContextProvider>
    );
  }

  // Cloud mode: Use Auth0

  // In Cloud mode, Auth0 config must be present
  if (!envConfig.auth0) {
    console.error('Auth0 config missing in cloud mode');
    return (
      <div>{`Authentication unavailable. Please contact support at ${SUPPORT_CONTACT_NUMBER}.`}</div>
    );
  }

  // Handle redirect callback to preserve location
  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    window.location.replace(appState?.returnTo || window.location.origin);
  };

  // Build Auth0 options once
  const auth0Opts: Auth0ProviderOptions = {
    domain: envConfig.auth0.domain,
    clientId: envConfig.auth0.clientId,
    cacheLocation: 'localstorage',
    cookieDomain: getCookieDomain(window.location.hostname),
    authorizationParams: {
      redirect_uri: window.location.origin,
      // Least-privilege default: read-only here; write is opted into per-request in the central
      // getAccessToken, only when active. auth0-spa-js unions every request with this default and
      // can never drop a scope — so baking in `write` is what forced read-only to be signalled
      // out-of-band, invisible to the SDK dedup key (clientId::audience::scope), letting a
      // concurrent write request coalesce onto and poison a view-only mint. A read-only default
      // keeps the two on distinct dedup keys, killing that race at the source.
      scope: AUTH_SCOPE_READ_ONLY,
      audience: envConfig.auth0.audience,
    },
    onRedirectCallback,
  };

  return (
    <Auth0Provider {...auth0Opts}>
      <AuthContextProvider>
        <AuthLoadingBoundary>{children}</AuthLoadingBoundary>
      </AuthContextProvider>
    </Auth0Provider>
  );
};
